import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Wallpaper, X, Loader2, Sparkles } from "lucide-react";
import axios from "axios";
import { resolveUrl } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WallpaperCard() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [imageUrl, setImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const previewRef = useRef(null);

  const build = async () => {
    setStatus("loading");
    setError(null);
    setImageUrl(null);
    try {
      const { data } = await axios.post(`${API}/wallpaper/generate`, {}, { timeout: 90000 });
      // Cache-bust with variant so the <img> refreshes even if URL somehow repeated
      const url = resolveUrl(data.url) + `?v=${data.variant}`;
      setImageUrl(url);
      setStatus("ready");
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail || e?.message || "Something went wrong.";
      setError(String(detail));
      setStatus("error");
    }
  };

  useEffect(() => { if (open && status === "idle") build(); }, [open]); // eslint-disable-line

  const download = async () => {
    if (!imageUrl) return;
    try {
      const r = await fetch(imageUrl);
      const blob = await r.blob();
      const bloburl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = bloburl;
      a.download = `bob-birthday-wallpaper-${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(bloburl), 1500);
    } catch (e) {
      // fallback direct
      window.open(imageUrl, "_blank");
    }
  };

  return (
    <>
      {/* Floating trigger — mirrors the ambient player pill, lives on the LEFT side. Icon-only on mobile. */}
      <motion.button
        data-testid="wallpaper-open"
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8, duration: 0.7 }}
        whileHover={{ y: -3, scale: 1.03 }}
        whileTap={{ scale: 0.96 }}
        className="fixed z-40 group flex items-center gap-2.5 rounded-full bg-white pl-2 pr-2 sm:pl-2.5 sm:pr-4 py-2 border border-[#EAF0F6] shadow-[0_20px_45px_-10px_rgba(11,25,44,0.18)]"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)", left: "calc(env(safe-area-inset-left, 0px) + 1rem)" }}
        aria-label="Download birthday wallpaper"
      >
        <span className="relative w-9 h-9 rounded-full bg-gradient-to-br from-[#FFD6E7] via-[#A7C7E7] to-[#7B9ACC] flex items-center justify-center">
          <Wallpaper className="w-4 h-4 text-[#0B192C]" />
        </span>
        <div className="hidden sm:flex flex-col items-start leading-tight">
          <span className="font-body text-[10px] uppercase tracking-[0.28em] text-[#7B9ACC]">
            Photo collage
          </span>
          <span className="font-hand text-sm text-[#0B192C]">
            Get your wallpaper →
          </span>
        </div>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="wp-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0B192C]/70 backdrop-blur-md px-4 py-6 overflow-y-auto"
            onClick={() => setOpen(false)}
            data-testid="wallpaper-modal"
          >
            <motion.div
              initial={{ y: 30, scale: 0.95, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 20, scale: 0.97, opacity: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 22 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md rounded-3xl bg-white p-4 sm:p-5 max-h-[92vh] overflow-y-auto"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-[#EAF0F6] flex items-center justify-center text-[#334E68] z-10"
                aria-label="Close"
                data-testid="wallpaper-close"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="pr-10">
                <p className="font-body text-[10px] uppercase tracking-[0.35em] text-[#7B9ACC] inline-flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> Photo collage • your souvenir wallpaper
                </p>
                <h3 className="mt-1 font-display font-black text-xl sm:text-2xl text-[#0B192C] leading-tight">
                  Your birthday, on your lock screen.
                </h3>
                <p className="mt-1.5 font-body text-xs sm:text-sm text-[#334E68]">
                  Your real photos, stitched into a keepsake wallpaper — no filters, no face changes, just you.
                </p>
              </div>

              <div className="mt-4 relative rounded-2xl overflow-hidden bg-[#0B192C] w-full flex items-center justify-center" style={{ maxHeight: "42vh", aspectRatio: "9 / 16" }}>
                {status === "loading" && (
                  <div className="flex flex-col items-center gap-3 text-[#A7C7E7] px-6 text-center">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="font-hand text-lg text-[#F7F9FC]">stitching your memories…</span>
                    <span className="font-body text-[10px] uppercase tracking-[0.3em] text-[#7B9ACC]">
                      just a few seconds
                    </span>
                  </div>
                )}
                {status === "error" && (
                  <div className="p-6 text-center">
                    <p className="font-hand text-[#F7F9FC] text-xl">
                      Couldn't build it right now
                    </p>
                    <p className="mt-2 font-body text-xs text-[#A7C7E7] break-words">
                      {error || "Try again in a moment."}
                    </p>
                    <button
                      onClick={build}
                      className="mt-4 rounded-full bg-[#F7F9FC] text-[#0B192C] px-4 py-2 text-sm"
                    >
                      Retry
                    </button>
                  </div>
                )}
                {status === "ready" && imageUrl && (
                  <img
                    ref={previewRef}
                    src={imageUrl}
                    alt="Bob's AI birthday wallpaper"
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="wallpaper-preview"
                  />
                )}
              </div>

              <div className="mt-4 flex flex-col sm:flex-row gap-2.5">
                <button
                  onClick={download}
                  disabled={status !== "ready"}
                  data-testid="wallpaper-download"
                  className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[#0B192C] text-white px-5 py-3 font-body text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#050914] transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button
                  onClick={build}
                  disabled={status === "loading"}
                  data-testid="wallpaper-regenerate"
                  className="flex-1 flex items-center justify-center gap-2 rounded-full border border-[#EAF0F6] text-[#334E68] px-5 py-3 font-body text-sm hover:bg-[#F7F9FC] transition-colors disabled:opacity-40"
                >
                  <Sparkles className="w-4 h-4" />
                  {status === "loading" ? "Generating…" : "Regenerate"}
                </button>
              </div>

              <p className="mt-2.5 text-center font-hand text-xs sm:text-sm text-[#7B9ACC]">
                Long-press the image to save it, then set as wallpaper 📱
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
